import { createElement } from "react";
import type { AutocompleteOption } from "@/components/Autocomplete";
import { SupplierLogo } from "@/components/GeschaeftspartnerLogo";
import { api } from "@/lib/apiClient";
import type { HeroSupplier, HeroSupplierContact } from "@/lib/orders";
import type { OrderSupplierState } from "@/lib/orderDraftUtils";

function normName(name: string): string {
  return (name || "").trim().toLowerCase();
}

/**
 * In der Historie kann derselbe Lieferant mit verschiedenen supplier_id vorkommen
 * (GROUP BY id+name+email). Für die Auswahlliste reicht ein Eintrag pro Name.
 */
function dedupeSuppliersByName<
  T extends { id?: string | number | null; name?: string | null; order_count?: number },
>(rows: T[]): T[] {
  const map = new Map<string, T>();
  for (const row of rows) {
    const name = (row.name || "").trim();
    if (!name) continue;
    const key = normName(name);
    const cur = map.get(key);
    if (!cur) {
      map.set(key, row);
      continue;
    }
    const curCount = Number(cur.order_count ?? 0);
    const rowCount = Number(row.order_count ?? 0);
    if (rowCount > curCount) {
      map.set(key, row);
      continue;
    }
    if (rowCount === curCount) {
      const curHasId = cur.id != null && String(cur.id).trim() !== "";
      const rowHasId = row.id != null && String(row.id).trim() !== "";
      if (rowHasId && !curHasId) map.set(key, row);
    }
  }
  return [...map.values()];
}

export const COMPANY_EMAIL_CONTACT_LABEL = "Allgemeine E-Mail";
export const COMPANY_EMAIL_SALUTATION = "Sehr geehrte Damen und Herren";

function contactDedupeKey(c: HeroSupplierContact): string {
  if (c.is_company_email) return `company:${(c.email || "").trim().toLowerCase()}`;
  return (c.email || c.value || c.label || "").trim().toLowerCase();
}

function dedupeContacts(list: HeroSupplierContact[]): HeroSupplierContact[] {
  const seen = new Set<string>();
  const out: HeroSupplierContact[] = [];
  for (const c of list) {
    const key = contactDedupeKey(c);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

export function companyEmailContact(email: string): HeroSupplierContact | null {
  const mail = (email || "").trim();
  if (!mail) return null;
  return {
    label: COMPANY_EMAIL_CONTACT_LABEL,
    value: `company:${mail.toLowerCase()}`,
    first_name: "",
    last_name: "",
    email: mail,
    title: "",
    is_company_email: true,
  };
}

/** Personen bleiben unverändert; die Kontakt-Mail kommt zusätzlich ans Ende. */
export function withCompanyEmailChoice(
  contacts: HeroSupplierContact[],
  companyEmail: string | null | undefined,
  query: string,
): HeroSupplierContact[] {
  const people = contacts.filter((c) => !c.is_company_email);
  const fromList = contacts.find((c) => c.is_company_email && (c.email || "").trim());
  const option = companyEmailContact(fromList?.email || companyEmail || "");
  if (!option) return people;
  const term = (query || "").trim().toLowerCase();
  if (term && !`${COMPANY_EMAIL_CONTACT_LABEL} ${option.email}`.toLowerCase().includes(term)) {
    return people;
  }
  return [...people, option];
}

export function defaultContactWhenNoPersons(
  options: AutocompleteOption<HeroSupplierContact>[],
): HeroSupplierContact | null {
  if (options.some((opt) => !opt.value.is_company_email)) return null;
  return options.find((opt) => opt.value.is_company_email)?.value ?? null;
}

export function supplierFieldsFromContact(
  c: HeroSupplierContact,
): Pick<
  OrderSupplierState,
  "anrede" | "email" | "contact_first_name" | "contact_last_name" | "person"
> {
  if (c.is_company_email) {
    return {
      anrede: COMPANY_EMAIL_SALUTATION,
      email: (c.email || "").trim(),
      contact_first_name: "",
      contact_last_name: "",
      person: "",
    };
  }
  return {
    anrede: (c.title || "").trim(),
    email: (c.email || "").trim(),
    contact_first_name: (c.first_name || "").trim(),
    contact_last_name: (c.last_name || "").trim(),
    person: (c.label || "").split("·")[0]?.trim() || "",
  };
}

function toSupplierValue(s: HeroSupplier): HeroSupplier {
  return {
    id: s.id,
    name: s.name || "",
    email: (s.email || "").trim() || undefined,
    geschaeftspartner_id: s.geschaeftspartner_id ?? null,
    hero_contact_id: s.hero_contact_id ?? null,
    order_count: s.order_count,
    has_logo: Boolean(s.has_logo),
    logo_updated_at: s.logo_updated_at ?? null,
  };
}

export function supplierLogoState(
  source: {
    geschaeftspartner_id?: number | null;
    has_logo?: boolean | null;
    logo_updated_at?: string | null;
  } | null | undefined,
): Pick<OrderSupplierState, "geschaeftspartner_id" | "has_logo" | "logo_updated_at"> {
  return {
    geschaeftspartner_id: source?.geschaeftspartner_id ?? null,
    has_logo: Boolean(source?.has_logo),
    logo_updated_at: source?.logo_updated_at ?? null,
  };
}

function supplierLeading(s: {
  name?: string | null;
  geschaeftspartner_id?: number | null;
  has_logo?: boolean | null;
  logo_updated_at?: string | null;
}) {
  const name = (s.name || "").trim();
  if (!name) return undefined;
  return createElement(SupplierLogo, {
    name,
    partnerId: s.geschaeftspartner_id,
    hasLogo: s.has_logo,
    logoUpdatedAt: s.logo_updated_at,
    className: "h-7 w-7 rounded-md text-[10px]",
  });
}

/** Lieferanten aus App-Kontakten; häufig bestellte zuerst. */
export async function loadOrderSupplierOptions(
  query: string,
): Promise<AutocompleteOption<HeroSupplier>[]> {
  const term = (query || "").trim();
  const usp = new URLSearchParams();
  if (term) usp.set("q", term);
  usp.set("limit", term ? "40" : "25");
  const raw = await api.get<HeroSupplier[]>(`/orders/hero/suppliers?${usp}`);
  const rows = (raw || [])
    .filter((s) => (s.name || "").trim() !== "")
    .map(toSupplierValue);

  return dedupeSuppliersByName(rows).map((s) => {
    const value = toSupplierValue(s);
    const name = value.name || "";
    return {
      key: `supplier-${normName(name)}`,
      label: value.order_count ? `${name} · ${value.order_count}×` : name || "-",
      description: value.email || undefined,
      leading: supplierLeading(value),
      value,
    };
  });
}

function contactDisplayName(c: {
  first_name?: string | null;
  last_name?: string | null;
  label?: string | null;
}): string {
  const first = (c.first_name || "").trim();
  const last = (c.last_name || "").trim();
  let name = [first, last].filter(Boolean).join(" ").trim();
  if (!name) {
    const raw = (c.label || "").split("·")[0]?.trim() || "";
    name = raw.replace(/\s+\([^)]+\)\s*$/, "").trim() || raw;
  }
  return name || "Ansprechpartner";
}

function contactToOption(c: HeroSupplierContact): AutocompleteOption<HeroSupplierContact> {
  const email = (c.email || "").trim();
  if (c.is_company_email) {
    return {
      key: `company-email-${email.toLowerCase()}`,
      label: COMPANY_EMAIL_CONTACT_LABEL,
      description: email || undefined,
      value: { ...c, label: COMPANY_EMAIL_CONTACT_LABEL, is_company_email: true },
    };
  }
  return {
    key: `contact-${(email || c.value || contactDisplayName(c)).toLowerCase()}`,
    label: contactDisplayName(c),
    description: email || undefined,
    value: c,
  };
}

function fallbackContactItems(supplier: OrderSupplierState | null): HeroSupplierContact[] {
  if (!supplier) return [];
  const email = (supplier.email || "").trim();
  const first = (supplier.contact_first_name || "").trim();
  const last = (supplier.contact_last_name || "").trim();
  const name = [first, last].filter(Boolean).join(" ").trim() || (supplier.person || "").trim();
  // Ohne Personenname ist das die allgemeine Mail und kein Ansprechpartner.
  if (!name) return [];
  return [
    {
      label: contactDisplayName({
        first_name: first,
        last_name: last,
      }),
      value: email || name,
      first_name: first,
      last_name: last,
      email,
      title: supplier.anrede || "",
    },
  ];
}

function contactsToOptions(
  contacts: HeroSupplierContact[],
  companyEmail: string | null | undefined,
  query: string,
): AutocompleteOption<HeroSupplierContact>[] {
  return withCompanyEmailChoice(dedupeContacts(contacts), companyEmail, query).map(contactToOption);
}

function mapSupplierContactRows(list: HeroSupplierContact[]): HeroSupplierContact[] {
  return list.flatMap((c) => {
    if (c.is_company_email) {
      const company = companyEmailContact(c.email || "");
      return company ? [company] : [];
    }
    const hasStructured = !!(c.first_name || c.last_name);
    const first = hasStructured
      ? c.first_name || ""
      : (c.label || "").split("·")[0]?.trim().split(" ").slice(0, -1).join(" ") || "";
    const last = hasStructured ? c.last_name || "" : "";
    const email = (c.email || "").trim();
    const title = (c.title || "").trim();
    const name = [first, last].filter(Boolean).join(" ").trim();
    return [
      {
        label: contactDisplayName({ first_name: first, last_name: last, label: c.label }),
        value: email || name || c.value,
        first_name: first,
        last_name: last,
        email,
        title,
      } satisfies HeroSupplierContact,
    ];
  });
}

export interface SupplierContactChoiceLoad {
  options: AutocompleteOption<HeroSupplierContact>[];
  /** False, wenn die Ansprechpartner nicht geladen werden konnten. */
  contactsLoaded: boolean;
}

/** Wie `fetchOrderSupplierContacts` in bestellungen.js. */
export async function loadSupplierContactChoices(
  supplier: OrderSupplierState | null,
  query: string,
): Promise<SupplierContactChoiceLoad> {
  const supId = supplier?.id != null ? String(supplier.id) : "";
  if (!supId || supId.startsWith("name:")) {
    const people = fallbackContactItems(supplier);
    const companyEmail =
      (supplier?.company_email || "").trim() || (people.length ? "" : supplier?.email || "");
    return {
      contactsLoaded: true,
      options: contactsToOptions(people, companyEmail, query),
    };
  }

  const usp = new URLSearchParams({ supplier_id: supId });
  if (query.trim()) usp.set("q", query.trim());
  try {
    const list = await api.get<HeroSupplierContact[]>(`/orders/hero/supplier-contacts?${usp}`);
    return {
      contactsLoaded: true,
      options: contactsToOptions(mapSupplierContactRows(list || []), supplier?.company_email, query),
    };
  } catch {
    return {
      contactsLoaded: false,
      options: contactsToOptions([], supplier?.company_email, query),
    };
  }
}

export async function loadOrderSupplierContactOptions(
  supplier: OrderSupplierState | null,
  query: string,
): Promise<AutocompleteOption<HeroSupplierContact>[]> {
  const loaded = await loadSupplierContactChoices(supplier, query);
  return loaded.options;
}

export function supplierToAutocompleteOption(
  supplier: OrderSupplierState | null,
): AutocompleteOption<HeroSupplier> | null {
  if (!supplier) return null;
  const name = (supplier.name || "").trim();
  if (!name && supplier.id == null) return null;
  const id = supplier.id != null && String(supplier.id).trim() !== "" ? supplier.id : `name:${normName(name)}`;
  const value: HeroSupplier = {
    id,
    name,
    email: (supplier.email || "").trim() || undefined,
    geschaeftspartner_id: supplier.geschaeftspartner_id ?? null,
    has_logo: Boolean(supplier.has_logo),
    logo_updated_at: supplier.logo_updated_at ?? null,
  };
  return {
    key: `supplier-${normName(name)}`,
    label: name || String(id),
    description: (supplier.company_email || supplier.email || "").trim() || undefined,
    leading: supplierLeading(value),
    value,
  };
}

export function contactToAutocompleteOption(
  supplier: OrderSupplierState | null,
): AutocompleteOption<HeroSupplierContact> | null {
  if (!supplier) return null;
  const email = (supplier.email || "").trim();
  const first = (supplier.contact_first_name || "").trim();
  const last = (supplier.contact_last_name || "").trim();
  const person = (supplier.person || "").trim();
  const name = [first, last].filter(Boolean).join(" ").trim() || person;
  if (!name && !email) return null;
  if (!name && email) {
    const company = companyEmailContact(email);
    return company ? contactToOption(company) : null;
  }
  const contact: HeroSupplierContact = {
    label: contactDisplayName({
      first_name: first,
      last_name: last,
    }),
    value: email || name,
    first_name: first,
    last_name: last,
    email,
    title: supplier.anrede || "",
  };
  return contactToOption(contact);
}
